import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { lazy, Suspense } from 'react';
import { ProtectedRoute } from './auth/ProtectedRoute';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Testing from './pages/Testing';
import Templates from './pages/Templates';
import Wings from './pages/Wings';
import WingEditor from './pages/WingEditor';
import Health from './pages/Health';

// Help Center pages (lazy-loaded to keep main bundle lean)
const ArticleList = lazy(() => import('./pages/help-center/ArticleList'));
const ArticleEditor = lazy(() => import('./pages/help-center/ArticleEditor'));
const Categories = lazy(() => import('./pages/help-center/Categories'));
const TicketList = lazy(() => import('./pages/help-center/TicketList'));
const TicketDetail = lazy(() => import('./pages/help-center/TicketDetail'));
const FeatureRequestList = lazy(() => import('./pages/help-center/FeatureRequestList'));
const FeatureRequestDetail = lazy(() => import('./pages/help-center/FeatureRequestDetail'));
const FAQManager = lazy(() => import('./pages/help-center/FAQManager'));
const MediaLibrary = lazy(() => import('./pages/help-center/MediaLibrary'));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <p className="text-text-secondary">Loading...</p>
    </div>
  );
}

export default function App() {
  const { isLoading, error } = useAuth0();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-alert">Authentication error: {error.message}</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ProtectedRoute>
        <Layout>
          <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/testing" element={<Testing />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/wings" element={<Wings />} />
              <Route path="/wings/new" element={<WingEditor />} />
              <Route path="/wings/:collectionId" element={<WingEditor />} />
              <Route path="/wings/:collectionId/:stepId" element={<WingEditor />} />
              <Route path="/health" element={<Health />} />

              {/* Help Center Admin */}
              <Route path="/help-center/articles" element={<ArticleList />} />
              <Route path="/help-center/articles/new" element={<ArticleEditor />} />
              <Route path="/help-center/articles/:id" element={<ArticleEditor />} />
              <Route path="/help-center/categories" element={<Categories />} />
              <Route path="/help-center/tickets" element={<TicketList />} />
              <Route path="/help-center/tickets/:id" element={<TicketDetail />} />
              <Route path="/help-center/features" element={<FeatureRequestList />} />
              <Route path="/help-center/features/:id" element={<FeatureRequestDetail />} />
              <Route path="/help-center/faqs" element={<FAQManager />} />
              <Route path="/help-center/media" element={<MediaLibrary />} />
            </Routes>
          </Suspense>
        </Layout>
      </ProtectedRoute>
    </BrowserRouter>
  );
}
